import { useEffect, useMemo, useState } from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
  Grid,
  CircularProgress,
  Box,
} from '@material-ui/core';

// data layer
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { getLoggedRole } from 'data-layer-cr/dist/authentication';

// redux
import { RootState } from 'src/store/rootReducer';
import { connect, ConnectedProps } from 'react-redux';
import {
  requestQuotationsMeasurements,
  requestSalesTrend,
} from 'src/store/metric/actions/metricActions';
import { getSubagents } from 'src/store/quotation/actions';
import {
  REQUEST_QUOTATIONS_MEASUREMENTS,
  REQUEST_SALES_TREND,
} from 'src/store/metric/requestIdentifiers';

// models
import QuotationsMeasurements from 'src/models/Chart/QuotationsMeasurements';
import SalesTrend from 'src/models/Chart/SalesTrend';
import { FilterOptionLicense, FilterOptionDate } from 'src/models/FilterMetric';
import { FilterOption } from 'src/models/Filter';

import {
  statusInspection,
  statusEmitting,
  statusEmitted,
} from 'src/models/Filter/filterData';

// constants
import {
  TOTAL_EMITTED_POLICIES,
  TOTAL_PENDING_EMISSIONS,
  TOTAL_PENDING_INSPECTIONS,
  QuotationStatusMetric,
} from 'src/constants/metricType';

// Enum
import { DisplayModes } from 'src/components/screens/Quotations/List/QuotationsList';

// component
import {
  FilterGroup,
  TabSelectProductType,
  CardsWithQuotationsByStatus,
  PieGraphics,
  LineGraphic,
} from './components';

// helpers
import {
  buildQueryFilterQuotationsMeasurements,
  buildQueryFilterSalesTrend,
  getInitialFilterDateState,
  metricFilterDateToDrawerFilterDate,
  metricFilterCurrentUserToDrawerFilterCurrentUser,
  metricFilterLicenseToDrawerFilterLicense,
  metricFilterSubagentToDrawerFilterSubagent,
  isQueriableSubagent,
} from './helpers';

// init filter state
import { licenseInitialFilter } from './initialFilterMetricData';

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    rootContainer: {
      paddingTop: 16,
      paddingBottom: 16,
      gap: 24,
    },
    insideTabSection: {
      [theme.breakpoints.up('md')]: {
        margin: '0px 60px',
      },
      [theme.breakpoints.up('xl')]: {
        margin: '0px 240px',
      },
    },
  }),
);

export const MetricTab = ({
  quotationsMeasurements,
  salesTrend,
  requestQuotationsMeasurementsAction,
  requestSalesTrendAction,
  licenses,
  userName,
  onAddFilterDrawer,
  handleChangeDisplayMode,
  handleSelectTab,
  getSubagentsAction,
  subAgentList,
}: Props): JSX.Element => {
  const classes = useStyle();
  const loggedUserRole = getLoggedRole();
  const getQuotationsMeasurementsStatus = useNetworkObserver(
    REQUEST_QUOTATIONS_MEASUREMENTS,
  );
  const requestSalesTrendStatus = useNetworkObserver(REQUEST_SALES_TREND);

  const filterDate = getInitialFilterDateState();

  const [filterDateSelected, setFilterDateSelected] =
    useState<FilterOptionDate>(filterDate.sevenDaysAgo);
  const [filterLicenseSelected, setFilterLicenseSelected] =
    useState<FilterOptionLicense>(
      licenseInitialFilter(loggedUserRole, licenses),
    );
  const [filterSubagentSelected, setFilterSubagentSelected] =
    useState<FilterOptionLicense>({
      description: '',
      queryKey: '',
    });
  const [isCheckedCurrentUserFilter, setIsCheckedCurrentUserFilter] =
    useState<boolean>(false);

  const defaultCheckUser = useMemo(
    () =>
      (filterLicenseSelected.queryKey === '' ||
        filterLicenseSelected.queryKey === 'default') &&
      loggedUserRole === 'AGE',
    [loggedUserRole, filterLicenseSelected],
  );

  useEffect(() => {
    const queryFilter = buildQueryFilterQuotationsMeasurements(
      {
        isCheckedCurrentUserFilter,
        filterDateSelected,
        filterLicenseSelected,
        filterSubagentSelected,
      },
      userName as string,
      loggedUserRole,
    );
    requestQuotationsMeasurementsAction(queryFilter);
  }, [
    isCheckedCurrentUserFilter,
    filterDateSelected,
    filterLicenseSelected,
    userName,
    loggedUserRole,
    filterSubagentSelected,
    requestQuotationsMeasurementsAction,
  ]);

  useEffect(() => {
    const queryFilter = buildQueryFilterSalesTrend(
      {
        isCheckedCurrentUserFilter,
        filterLicenseSelected,
        filterSubagentSelected,
      },
      userName as string,
      loggedUserRole,
    );
    requestSalesTrendAction(queryFilter);
  }, [
    isCheckedCurrentUserFilter,
    filterLicenseSelected,
    userName,
    loggedUserRole,
    filterSubagentSelected,
    requestSalesTrendAction,
  ]);

  const handleSelectDateFilter = (newSelectedDate: FilterOptionDate) =>
    setFilterDateSelected(newSelectedDate);

  const handleSelectLicenseFilter = (
    newFilterOptionLicense: FilterOptionLicense,
  ) => {
    setFilterSubagentSelected({ description: '', queryKey: '' });
    setFilterLicenseSelected(newFilterOptionLicense);
  };

  const handleSelectSubagentFilter = (
    newFilterOptionLicense: FilterOptionLicense,
  ) => setFilterSubagentSelected(newFilterOptionLicense);

  const handleCheckCurrentUserFilter = (isChecked: boolean) =>
    setIsCheckedCurrentUserFilter(isChecked);

  const handleResetFilters = () => {
    setFilterDateSelected(filterDate.sevenDaysAgo);
    setFilterLicenseSelected(licenseInitialFilter(loggedUserRole, licenses));
    setFilterSubagentSelected({ description: '', queryKey: '' });
    setIsCheckedCurrentUserFilter(false);
  };

  const handleOnClickSeeDetails = (
    quotationStatusMetric: QuotationStatusMetric,
  ) => {
    switch (quotationStatusMetric) {
      case TOTAL_EMITTED_POLICIES:
        onAddFilterDrawer(statusEmitted);
        break;
      case TOTAL_PENDING_EMISSIONS:
        onAddFilterDrawer(statusEmitting);
        break;
      case TOTAL_PENDING_INSPECTIONS:
        onAddFilterDrawer(statusInspection);
        break;
      default:
        onAddFilterDrawer(statusInspection);
    }
    onAddFilterDrawer(metricFilterDateToDrawerFilterDate(filterDateSelected));
    metricFilterCurrentUserToDrawerFilterCurrentUser(
      onAddFilterDrawer,
      isCheckedCurrentUserFilter,
      userName as string,
    );
    metricFilterLicenseToDrawerFilterLicense(
      onAddFilterDrawer,
      filterLicenseSelected,
    );
    metricFilterSubagentToDrawerFilterSubagent(
      onAddFilterDrawer,
      filterSubagentSelected,
    );
    handleChangeDisplayMode(DisplayModes.TableMode);
    handleSelectTab(0);
  };

  useEffect(() => {
    if (isQueriableSubagent(filterLicenseSelected, loggedUserRole)) {
      getSubagentsAction(filterLicenseSelected.queryKey);
    }
  }, [filterLicenseSelected, loggedUserRole, getSubagentsAction]);

  return (
    <Grid container className={classes.rootContainer}>
      {/* Filter Section */}
      <Grid item xs={12}>
        <FilterGroup
          data-testid="filter-group"
          filterDateList={filterDate}
          handleSelectDateFilter={handleSelectDateFilter}
          filterDateSelected={filterDateSelected}
          handleSelectLicenseFilter={handleSelectLicenseFilter}
          filterLicenseSelected={filterLicenseSelected}
          licenses={licenses}
          handleCheckCurrentUserFilter={handleCheckCurrentUserFilter}
          isCheckedCurrentUserFilter={
            isCheckedCurrentUserFilter || defaultCheckUser
          }
          userName={userName}
          handleResetFilters={handleResetFilters}
          subAgentList={subAgentList}
          filterSubagentSelected={filterSubagentSelected}
          handleSelectSubagentFilter={handleSelectSubagentFilter}
          isQueriableSubagent={isQueriableSubagent(
            filterLicenseSelected,
            loggedUserRole,
          )}
        />
      </Grid>
      {/* Tab Filter Section */}
      <Grid item xs={12}>
        <TabSelectProductType data-testid="tab-select-product-type" />
      </Grid>
      {(getQuotationsMeasurementsStatus === 'requesting' ||
        requestSalesTrendStatus === 'requesting') && (
        <Box
          display="flex"
          data-testid="loading"
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100%"
        >
          <CircularProgress size={24} />
        </Box>
      )}

      <Grid
        item
        xs={12}
        container
        style={{ gap: 30 }}
        data-testid="graphics-container"
      >
        {/* Inside tab section */}
        {getQuotationsMeasurementsStatus === 'success' && (
          <Grid item xs={12} container justifyContent="center">
            <CardsWithQuotationsByStatus
              data-testid="cards-with-quotations-by-status"
              listQuotationsByStatus={quotationsMeasurements}
              handleOnClickSeeDetails={handleOnClickSeeDetails}
            />
          </Grid>
        )}

        {/* Quotations neasurements section */}
        {getQuotationsMeasurementsStatus === 'success' && (
          <Grid item xs={12} container justifyContent="center">
            <PieGraphics quotationsMeasurements={quotationsMeasurements} />
          </Grid>
        )}
        {requestSalesTrendStatus === 'success' && (
          <Grid item xs={12} container justifyContent="center">
            <LineGraphic salesTrend={salesTrend} />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotationsMeasurements: QuotationsMeasurements[];
  salesTrend: SalesTrend[];
  licenses: GeneralCatalogItem[];
  subAgentList: GeneralCatalogItem[];
  userName: string | undefined;
} => ({
  quotationsMeasurements: state.metric.quotationsMeasurements,
  salesTrend: state.metric.salesTrend,
  licenses: state.profile.licenses,
  userName: state.profile.profile?.IdUser,
  subAgentList: state.quotation.subAgentList,
});

const reduxActions = {
  requestQuotationsMeasurementsAction: requestQuotationsMeasurements,
  requestSalesTrendAction: requestSalesTrend,
  getSubagentsAction: getSubagents,
};

const connector = connect(mapStateToProps, reduxActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onAddFilterDrawer: (newFilter: FilterOption) => void;
  handleChangeDisplayMode: (displayModes: DisplayModes) => void;
  handleSelectTab: (currenttPage: number) => void;
}

export default connector(MetricTab);
